import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { AnimatedButton } from "../../ui/AnimatedButton";
import {
  Add,
  ArrowBack,
  ContentCopy,
  Description,
  Email,
  LinkedIn,
} from "@mui/icons-material";
import StyledTypography from "../../ui/StyledTypography";
import { grey } from "@mui/material/colors";
import { useState } from "react";
import Header from "../../components/Header";
import Footer from "../home/Footer";
import { motion } from "framer-motion";

interface FAQ {
  question: string;
  answer: string;
}

const faqData: FAQ[] = [
  {
    question: "What technologies do you specialize in?",
    answer:
      "I specialize in front-end web development with technologies like React, TypeScript, HTML, CSS, and various JavaScript frameworks.",
  },
  {
    question: "What projects have you worked on?",
    answer:
      "I have worked on various projects ranging from e-commerce websites, portfolio sites, and data visualization tools, utilizing React and other modern libraries.",
  },
  {
    question: "What is your approach to writing clean code?",
    answer:
      "I follow principles like DRY (Don't Repeat Yourself), KISS (Keep It Simple, Stupid), and SOLID to write clean, maintainable code.",
  },
  {
    question: "How do you handle responsive design?",
    answer:
      "I use CSS media queries, flexible grid layouts, and mobile-first design principles to create responsive and adaptive user interfaces.",
  },
  {
    question: "What is your experience with version control systems?",
    answer:
      "I am proficient in using Git for version control. I use it for source code management, branching, and collaborating with other developers on team projects.",
  },
];

const Contact = () => {
  const [copied, setCopied] = useState(false);
  const email = "supremache@gmail.com";

  const handleCopy = () => {
    navigator.clipboard
      .writeText(email)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      })
      .catch((error) => {
        console.error("Failed to copy text: ", error);
      });
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        px: 2,
        pt: { xs: 4, md: 8 },
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <Header />

      <Container
        maxWidth="md"
        sx={{
          height: "100%",
          width: "100%",
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link to={"/"}>
            <AnimatedButton
              backgroundColor="rgb(15, 15, 15)"
              sx={{
                height: 40,
                width: 165,
                my: 4,
              }}
            >
              <ArrowBack sx={{ fontSize: 18 }} />
              Back To Home
            </AnimatedButton>
          </Link>
        </motion.div>

        <Stack spacing={4} mb={4}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <StyledTypography
              colors={[grey[200], grey[500]]}
              sx={{
                textAlign: "center",
                fontSize: 50,
                fontWeight: 500,
                position: "relative",
              }}
              component="div"
            >
              Get In {""}
              <StyledTypography
                colors={[grey[200], grey[500]]}
                style={{
                  fontSize: 50,
                  fontWeight: 500,
                  fontFamily: "'Modure Placeholder', serif",
                  fontStyle: "italic",
                  display: "inline",
                }}
                component="span"
              >
                touch
              </StyledTypography>
            </StyledTypography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Paper
              sx={{
                py: 1,
                px: 2,
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Don't hesitate to reach out if you have any questions.
              </Typography>

              <Stack
                gap={1}
                direction={"row"}
                flexWrap={"wrap"}
                justifyContent={"center"}
              >
                <AnimatedButton
                  aria-label="LinkedIn"
                  component="a"
                  href="https://www.linkedin.com/in/abdulrahman-othman-5a4a1331a/"
                  rel="noopener noreferrer"
                  backgroundColor="rgb(15, 15, 15)"
                  borderRadius="0.5rem"
                  sx={{ height: 40, width: 40, minWidth: 0 }}
                >
                  <LinkedIn sx={{ fontSize: 24 }} />
                </AnimatedButton>

                <AnimatedButton
                  aria-label="Resume"
                  href="../Resume.pdf"
                  component="a"
                  download={true}
                  backgroundColor="rgb(15, 15, 15)"
                  borderRadius="0.5rem"
                  sx={{ height: 40, width: 90, fontSize: 12, fontWeight: 300 }}
                >
                  <Description sx={{ fontSize: 24 }} />
                  Resume
                </AnimatedButton>

                <AnimatedButton
                  aria-label="Mail"
                  backgroundColor="rgb(15, 15, 15)"
                  borderRadius="0.5rem"
                  sx={{
                    height: 40,
                    width: copied ? 160 : 200,
                    fontSize: 12,
                    fontWeight: 300,
                  }}
                  onClick={handleCopy}
                >
                  {copied ? (
                    <ContentCopy sx={{ fontSize: 24 }} />
                  ) : (
                    <Email sx={{ fontSize: 24 }} />
                  )}
                  {copied ? "Copied To Clipboard" : email}
                </AnimatedButton>
              </Stack>
            </Paper>
          </motion.div>
        </Stack>

        <Divider sx={{ marginY: 4 }} />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <StyledTypography
            colors={[grey[200], grey[500]]}
            sx={{
              textAlign: "center",
              fontSize: 50,
              fontWeight: 500,
              position: "relative",
              mb: 4,
            }}
            component="div"
          >
            Frequently Asked {""}
            <StyledTypography
              colors={[grey[200], grey[500]]}
              style={{
                fontSize: 50,
                fontWeight: 500,
                fontFamily: "'Modure Placeholder', serif",
                fontStyle: "italic",
                display: "inline",
              }}
              component="span"
            >
              Questions
            </StyledTypography>
          </StyledTypography>

          {faqData.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
            >
              <Accordion disableGutters>
                <AccordionSummary
                  expandIcon={
                    <Add sx={{ color: "text.secondary", fontSize: "large" }} />
                  }
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                >
                  <Typography variant="body2" color="text.secondary">
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <StyledTypography
                    variant="body2"
                    colors={[grey[500], grey[900]]}
                  >
                    {faq.answer}
                  </StyledTypography>
                </AccordionDetails>
              </Accordion>
            </motion.div>
          ))}
        </motion.div>
      </Container>

      <Footer />
    </Container>
  );
};
export default Contact;
